import { useQuery } from '@tanstack/react-query';

import type { SessionState } from '@/shared/api/auth';
import type { PlatformPermissionKey } from '@/shared/api/platform';

import { sessionQueryOptions } from './session';

/**
 * The same check inline, for the controls a page shows or hides rather than a whole screen: a
 * reader with `contracts.view` sees a contract, and only `contracts.manage` gets the buttons that
 * change it. The API enforces it either way; this only keeps the console from offering what it
 * would then refuse.
 */
export function useHasPermission(permission: string): boolean {
  const session = useQuery(sessionQueryOptions());
  return session.data?.permissions.includes(permission) ?? false;
}

/**
 * The write keys of every module that `ReadOnlyBoundary` guards, one entry per module (13 §1.4,
 * `AM-137` point 1). A surface is mounted when the actor holds any one of its module's keys, so a
 * key a surface needs belongs in this table and never inline at the call site: this is the only
 * place that states what writing means for a module, and the only place to change when a module
 * gains a command.
 *
 * - `tickets`: the transition selector and the activity composer of `/tickets/:ticketId`. The
 *   composer is one control that writes either a comment visible to the requester
 *   (`comments.create`) or, for whoever also holds `work_notes.create`, an internal note, so both
 *   keys open it.
 * - `requests`: the commands of the triage bench (`/requests`). `requests.manage` is listed with
 *   `requests.triage` because the use case takes either of the two (`RequestAdministration.cs:367`),
 *   and `requests.convert` because converting is a command of the same panel, which keeps its own
 *   check for that key.
 * - `catalog`: the create, edit and delete panels of `/admin`'s catalog template, one component
 *   shared by the 22 pages that mount it; each of those pages guards itself with one of these keys,
 *   so every one of them is listed for the template to go on working as it does today.
 *   `maintenance_plans.manage` is the key of `/admin/maintenance-plans` (`AM-219`), which a role
 *   may hold without any `admin.*` write key.
 *
 * Every key is one of `PermissionCatalog` (08 §5.1), and none of them is seeded into the `auditor`
 * system role (`SystemRoles.cs:418-439`), which is what the rule is about.
 */
export const WRITE_PERMISSIONS = {
  tickets: ['tickets.manage', 'comments.create', 'work_notes.create'],
  requests: ['requests.triage', 'requests.manage', 'requests.convert'],
  catalog: [
    'admin.catalog.manage',
    'admin.billing.configure',
    'admin.calendars.manage',
    'admin.notifications.manage',
    'admin.organizations.manage',
    'admin.persons.manage',
    'admin.services.manage',
    'admin.sla.manage',
    'admin.teams.manage',
    'maintenance_plans.manage',
  ],
} as const;

/** A module of {@link WRITE_PERMISSIONS}: the only values `ReadOnlyBoundary` accepts. */
export type WritableModule = keyof typeof WRITE_PERMISSIONS;

/**
 * Whether the session's platform block holds this key of 08 §6.2 (`AM-134` point 11): the
 * boundary's own closed catalog, checked apart from `session.permissions` (a tenant's).
 */
export function hasPlatformPermission(
  session: SessionState | undefined,
  key: PlatformPermissionKey,
): boolean {
  return session?.platform.permissions.includes(key) ?? false;
}

/** Same check as a hook, for the platform area's own controls - mirrors {@link useHasPermission}. */
export function usePlatformPermission(key: PlatformPermissionKey): boolean {
  const session = useQuery(sessionQueryOptions());
  return hasPlatformPermission(session.data, key);
}
